import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Missed tasks — the one piece of the old 5-section Performance dashboard
// worth keeping (CJ 2026-07-24 declutter review, ★5): what didn't get done,
// so a manager can act. Streaks/patterns/cherry-pick reporting was cut as
// noise; per-person reliability stays on the employee profile.
export function MissedTasksView({ locationId, locationName }) {
  const [missed, setMissed] = useState(null);
  const [err, setErr] = useState('');

  // D104: the catch used to be `.catch(() => setMissed([]))`, which rendered a
  // failed fetch as an empty list — i.e. "nothing was missed". Of the four
  // silent failures in the D103 audit this was the only one that reported GOOD
  // NEWS on failure: a manager checking whether anything slipped was told
  // nothing had, when the truth was that we could not tell. "None" and
  // "couldn't check" must not look the same.
  function load() {
    setMissed(null);
    setErr('');
    api('GET', `/api/dashboard/missed-tasks?location_id=${locationId}&days=7`)
      .then(setMissed)
      .catch((e) => {
        let m = (e && e.message) || String(e);
        try { m = JSON.parse(m).error || m; } catch { /* not JSON */ }
        setErr(m);
      });
  }

  useEffect(load, [locationId]);

  if (err) {
    return (
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>Couldn’t check for missed tasks</div>
        <div className="text-xs text-red-700" style={{ marginBottom: 8 }}>{err}</div>
        <div className="text-xs text-gray-500" style={{ marginBottom: 10 }}>
          This is not the same as “nothing was missed” — the check itself failed, so treat it as unknown.
        </div>
        <button className="btn-secondary text-xs" onClick={load}>Try again</button>
      </div>
    );
  }
  if (missed === null) return <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>;

  const chronicallyMissed = (() => {
    const counts = {};
    missed.forEach(day => {
      day.tasks.forEach(t => {
        if (!counts[t.id]) counts[t.id] = { title: t.title, count: 0 };
        counts[t.id].count++;
      });
    });
    return Object.values(counts).filter(t => t.count >= 3).sort((a, b) => b.count - a.count);
  })();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-bold text-lg text-ink mb-1">Missed tasks</h2>
        <p className="text-sm text-gray-500">Past 7 days · {locationName}</p>
      </div>

      {chronicallyMissed.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Missed 3+ Days This Week</h3>
          <div className="bg-white rounded-xl border border-red-100 overflow-hidden shadow-sm">
            {chronicallyMissed.map((t, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0">
                <span className="text-red-400 text-lg">🔁</span>
                <p className="text-sm font-medium text-ink flex-1">{t.title}</p>
                <span className="text-xs font-bold text-red-600">{t.count}/7 days missed</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Daily Breakdown</h3>
        <div className="space-y-2">
          {[...missed].reverse().map(day => (
            <div key={day.date} className={`rounded-xl border p-3 ${day.missed_count > 0 ? 'border-red-100 bg-red-50' : 'border-green-100 bg-green-50'}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-ink">
                  {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </p>
                <span className={`text-xs font-bold ${day.missed_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {day.missed_count > 0 ? `${day.missed_count} missed` : '✓ All done'}
                </span>
              </div>
              {day.missed_count > 0 && (
                <p className="text-xs text-gray-500 truncate">
                  {day.tasks.slice(0, 3).map(t => t.title).join(', ')}{day.tasks.length > 3 ? ` +${day.tasks.length - 3} more` : ''}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
